import { useSearch } from "@tanstack/react-router";
import { Modal } from "antd";
import queryClient from "api/queryClient.ts";
import { useExportCommissionsMutation, useGetCommissionsQuery } from "api/TenantSlug/Commission/hooks.ts";
import {
    useDestroyCommissionGroupMutation,
    useUpdateCommissionGroupMutation,
} from "api/TenantSlug/CommissionGroup/hooks.ts";
import { AddCommissionDrawer } from "components/Drawers/AddCommissionDrawer/AddCommissionDrawer.tsx";
import { DetailCommissionDrawer } from "components/Drawers/DetailCommissionDrawer/DetailCommissionDrawer.tsx";
import { EditCommissionDrawer } from "components/Drawers/EditCommissionDrawer/EditCommissionDrawer.tsx";
import { EditCommissionGroupDrawer } from "components/Drawers/EditCommissionGroupDrawer/EditCommissionGroupDrawer.tsx";
import { userGroup } from "components/Tables/CommissionGroupTable/temp.ts";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useUser } from "hooks/useUser.ts";
import { COMMISSIONS_PRIORITY_SORT } from "localConstants.ts";
import { FC, Key, useEffect, useState, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { CommissionType, PermissionEnum } from "types/enums.ts";
import { DrawerId, TableId } from "types/models.ts";

import CommissionData = App.Data.CommissionData;
import GetCommissionResponse = App.Http.Responses.Commission.GetCommissionResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetCommissionsResponse = App.Http.Responses.Commission.GetCommissionsResponse;
import CascadeData = App.Data.CascadeData;

interface Props {
    offer?: App.Data.OfferData;
    cascade?: CascadeData;
    columns: TableColumnType<CommissionData>[];
    expandedColumns: TableColumnType<CommissionData>[];
    isLoading?: boolean;
    editPermission?: PermissionEnum;
    commissionGroupId?: string | null;
    showOnlyEnabledCommissions?: boolean;
    allowAddCommission?: boolean;
    onRefetchTable?: (refetchFn: () => void) => void;
    expandButtonRef?: React.RefObject<() => void>;
    onExpandChange?: (isExpanded: boolean) => void;
    scrollY?: number | string;
}

interface SearchParams {
    [`editCommission-id`]: string;
    [`detailCommission-id`]: string;
    [`editCommissionGroup-id`]: string;
}

export const SimpleCommissionsTable: FC<Props> = ({
    offer,
    cascade,
    columns,
    expandedColumns,
    isLoading,
    editPermission,
    commissionGroupId,
    showOnlyEnabledCommissions,
    allowAddCommission = true,
    onRefetchTable,
    expandButtonRef,
    onExpandChange,
    scrollY,
}) => {
    const permissionsUtils = usePermission();
    const user = useUser();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const [expandModal, setExpandModal] = useState(false);

    // Notify parent component when expand state changes
    useEffect(() => {
        onExpandChange?.(expandModal);
    }, [expandModal, onExpandChange]);
    const exportState = useRecoilValue(exportAtom(TableId.CommissionsTable));
    const { editingRowId, filter, page } = useRecoilValue(tableAtom<CommissionData>()(TableId.CommissionsTable));
    const fetchCommissionListQuery = useGetCommissionsQuery("commissions", {
        page: { number: page?.number, size: page?.size },
        filter: {
            filter: {
                ...(offer
                    ? {
                          "offer.id": offer.id,
                          ...((user.isAffiliate() || showOnlyEnabledCommissions) && { is_enabled: true }),
                      }
                    : {}),
                ...(cascade ? { "offer.id": cascade.offer_id, cascade_id: cascade.id } : {}),
                ...(filter?.filter ?? {}),
                commission_type: commissionGroupId || user.isAffiliate() ? CommissionType.User : CommissionType.Tenant,
                ...(commissionGroupId && {
                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                }),
            },
            sort: filter?.sort ?? COMMISSIONS_PRIORITY_SORT,
            include:
                "offer,user,cascade,parent,parent.user,offer.country,children,children.commissionGroup,commissionGroup",
        },
    });
    const destroyFormStructureMutation = useDestroyCommissionGroupMutation();
    const updateFormStructureMutation = useUpdateCommissionGroupMutation(editingRowId || "");

    const exportCommissionsMutation = useExportCommissionsMutation();

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<CommissionData>(
        {
            entityName: "commission",
            editPermission: editPermission,
            detailPermission: PermissionEnum.CommissionView,
            detailDrawerId: DrawerId.EditCommission,
            detailDrawerParam: `${DrawerId.EditCommission}-id`,
        },
        {
            additionalActions: [
                {
                    type: "drawer",
                    drawerId: DrawerId.DetailCommission,
                    drawerParam: `${DrawerId.DetailCommission}-id`,
                    permission: PermissionEnum.CommissionView,
                    condition: () => (editPermission ? !permissionsUtils.hasPermission(editPermission) : true),
                },
            ],
        }
    );

    const { tableData, tableState, exportData, refetchData, editTableData, deleteTableData } =
        useTableDataApiOperations<GetCommissionsResponse, GetCommissionResponse, CommissionData, BaseExportRequest>(
            TableId.CommissionsTable,
            fetchCommissionListQuery,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            destroyFormStructureMutation,
            updateFormStructureMutation,
            exportCommissionsMutation
        );

    onRefetchTable?.(refetchData);

    // Add conversion column to expanded columns
    const finalExpandedColumns = useMemo(() => {
        if (!expandedColumns.length) return expandedColumns;

        // Find the index of the description column to insert conversion column after it
        const descriptionIndex = expandedColumns.findIndex((col) => col.key === "description");
        if (descriptionIndex === -1) return expandedColumns;

        const conversionColumn: TableColumnType<CommissionData> = {
            title: <ColumnHeaderTitle columnTitle={tG("conversion")} />,
            dataIndex: "conversion_type",
            key: "conversion_type",
            sorter: false,
            render: (type: string) => <EmptyCell>{type}</EmptyCell>,
            ellipsis: true,
            width: 120,
            filterable: false,
        };

        // Insert conversion column after description
        const newColumns = [...expandedColumns];
        newColumns.splice(descriptionIndex + 1, 0, conversionColumn);
        return newColumns;
    }, [expandedColumns, tG]);

    // Expose the expand modal trigger function via ref
    useEffect(() => {
        if (expandButtonRef && expandButtonRef.current !== undefined) {
            (expandButtonRef as React.MutableRefObject<() => void>).current = () => setExpandModal(true);
        }
    }, [expandButtonRef]);

    return (
        <>
            {!commissionGroupId && permissionsUtils.hasPermission(PermissionEnum.CommissionCreate) && (
                <AddCommissionDrawer onSubmit={() => refetchData()} offerId={offer?.id} />
            )}
            {permissionsUtils.hasPermission(PermissionEnum.CommissionGroupUpdate) && (
                <>
                    {searchParams[`${DrawerId.EditCommissionGroup}-id`] && (
                        <EditCommissionGroupDrawer
                            commissionGroupId={searchParams[`${DrawerId.EditCommissionGroup}-id`]}
                        />
                    )}
                </>
            )}
            {searchParams[`${DrawerId.EditCommission}-id`] && (
                <EditCommissionDrawer
                    commissionId={searchParams[`${DrawerId.EditCommission}-id`]}
                    onSubmit={() => {
                        queryClient.invalidateQueries({
                            queryKey: ["getCommissions"],
                        });
                        setTimeout(() => {
                            refetchData();
                        }, 100); //dirty hot fix - why not working without delay?? TODO refactor whole table refetch logic bcs its shit due to antd..
                    }}
                />
            )}
            {searchParams[`${DrawerId.DetailCommission}-id`] && (
                <DetailCommissionDrawer commissionId={searchParams[`${DrawerId.DetailCommission}-id`]} />
            )}
            <Table
                tableId={TableId.CommissionsTable}
                data={tableData || []}
                dataSource={tableData?.commissions ?? []}
                columnConfig={columns}
                loading={!tableData?.commissions || isLoading || fetchCommissionListQuery.isPending}
                onRefresh={refetchData}
                showRefetchButton={true}
                enableTopBar={false}
                defaultDelete={deleteTableData}
                defaultOnUpdate={editTableData}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) =>
                    editPermission && permissionsUtils.hasPermission(editPermission)
                        ? getRowHandlers(record, rowIndex)
                        : {}
                }
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                expandable={{ childrenColumnName: "antdChildren" }}
                addEntityData={
                    !commissionGroupId && allowAddCommission
                        ? {
                              drawerId: DrawerId.AddCommission,
                              permission: PermissionEnum.CommissionCreate,
                          }
                        : undefined
                }
                enableWordWrap={true}
                onExport={() =>
                    exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page,
                        filter: {
                            filter: {
                                ...(tableState.filter?.filter ?? {}),
                                commission_type: commissionGroupId ? CommissionType.User : CommissionType.Tenant,
                                ...(commissionGroupId && {
                                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                                }),
                            },
                        },
                        delimiter: exportState.delimiter,
                    })
                }
                sticky={{ offsetHeader: 0 }}
                autoScrollWidth={true}
                scroll={scrollY ? { y: scrollY } : undefined}
            />
            {expandModal && (
                <Modal
                    open={expandModal}
                    onCancel={() => setExpandModal(false)}
                    width="90vw"
                    footer={null}
                    classNames={{ content: "h-[90vh] w-full" }}
                    styles={{ body: { height: "100%", overflow: "auto" } }}
                >
                    <Table
                        tableId={TableId.CommissionsTableExpanded}
                        data={tableData || []}
                        dataSource={tableData?.commissions ?? []}
                        columnConfig={finalExpandedColumns}
                        loading={!tableData?.commissions || isLoading || fetchCommissionListQuery.isPending}
                        onRefresh={refetchData}
                        showRefetchButton={true}
                        enableTopBar={true}
                        defaultDelete={deleteTableData}
                        defaultOnUpdate={editTableData}
                        rowKey={(record) => record.id as Key}
                        onRow={(record, rowIndex) =>
                            editPermission && permissionsUtils.hasPermission(editPermission)
                                ? getRowHandlers(record, rowIndex)
                                : {}
                        }
                        rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                        expandable={{ childrenColumnName: "antdChildren" }}
                        addEntityData={
                            !commissionGroupId && allowAddCommission
                                ? {
                                      drawerId: DrawerId.AddCommission,
                                      permission: PermissionEnum.CommissionCreate,
                                  }
                                : undefined
                        }
                        enableWordWrap={true}
                        onExport={() =>
                            exportData({
                                extension: exportState.extension,
                                headers: exportState.headers,
                                export_all: exportState.range === "all",
                                page,
                                filter: {
                                    filter: {
                                        ...(tableState.filter?.filter ?? {}),
                                        commission_type: commissionGroupId
                                            ? CommissionType.User
                                            : CommissionType.Tenant,
                                        ...(commissionGroupId && {
                                            commission_group_id:
                                                commissionGroupId === userGroup.id ? "" : commissionGroupId,
                                        }),
                                    },
                                },
                                delimiter: exportState.delimiter,
                            })
                        }
                        autoScrollWidth={true}
                    />
                </Modal>
            )}
        </>
    );
};
